/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit } from '@angular/core';
import { Logger } from '@app/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Website } from '@app/settings/websites/website.model';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { WebsitesService } from '@app/settings/websites/websites.service';
import { ApiQuery } from '@app/core/http/api-query';
import { Locale } from '@app/settings/locales/locales.model';
import { LocalesService } from '@app/settings/locales/locales.service';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';

const log = new Logger('WebsitesFormComponent');

@Component({
  selector: 'app-websites-form',
  templateUrl: './websites-form.component.html',
  styleUrls: ['./websites-form.component.scss'],
})
export class WebsitesFormComponent implements OnInit {
  @Input() website: Website = null;
  form: FormGroup;

  constructor(
    public modal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private websitesService: WebsitesService,
    private localesService: LocalesService
  ) {}

  ngOnInit() {
    this.createForm();
    this.patchForm();
  }

  /**
   * Праща данните към API-то
   */
  save(): void {
    if (this.website && this.website.id) {
      const website: Website = this.website.deserialize(this.formatPost());
      this.websitesService.update(website).subscribe((res) => this.modal.close('Save click'));
    } else {
      const website: Website = new Website().deserialize(this.formatPost());
      this.websitesService.store(website).subscribe((res) => this.modal.close(res));
    }
  }

  test(form: any) {
    log.debug(form);
  }

  private createForm(): void {
    const webhookActive = new FormControl(false);
    const webhookUrl = new FormControl(null);
    const webhookSecret = new FormControl(null);

    webhookActive.valueChanges.subscribe((val) => {
      if (val) {
        webhookUrl.setValidators([Validators.required]);
        webhookSecret.setValidators([Validators.required]);
      } else {
        webhookUrl.clearValidators();
        webhookSecret.clearValidators();
      }
      webhookUrl.updateValueAndValidity();
      webhookSecret.updateValueAndValidity();
    });

    this.form = this.formBuilder.group({
      id: [null],
      company_id: [null, [Validators.required]],
      company: [null],
      name: [null, [Validators.required]],
      url: [null, [Validators.required]],
      locales: new FormArray([]),
      active: [true],
      webhook_active: webhookActive,
      webhook_url: webhookUrl,
      webhook_secret: webhookSecret,
    });
  }

  private patchForm() {
    // Първо взимаме всички locales
    const localesQuery = new ApiQuery().setLimit(99999);
    this.localesService.index(localesQuery).subscribe((locales: PaginatedResponse) => {
      const localesArray: FormArray = <FormArray>this.form.controls.locales;
      locales.data.forEach((locale: Locale) => {
        const localeFormGroup = this.formBuilder.group({
          id: locale.id,
          locale: locale.locale,
          name: locale.name,
          selected: false,
        });
        localesArray.push(localeFormGroup);
      });

      /*
       * после гетва website-а и го пълни
       */
      if (this.website && this.website.id) {
        /*
         * Гетва website-a от сървиса по ID
         * Тук се подават като параметри всички includes(relations), които са нужни
         */
        const apiQuery: ApiQuery = new ApiQuery().setLimit(0).addIncludes('locales', 'company');
        this.websitesService.show(this.website.id, apiQuery).subscribe((website: Website) => {
          setTimeout(() => {
            // тук се patch-ват само locales
            website.locales.forEach((locale: Locale) => {
              this.form.get('locales')['controls'].forEach((localeControl: any) => {
                if (localeControl.get('id').value === locale.id) {
                  localeControl.get('selected').setValue(true);
                }
              });
            });

            // трябва locales да не се пачват понеже са пачнати по-горе отделно
            delete website.locales;
            this.form.patchValue(website);
          });
        });
      } else if (this.website) {
        this.form.patchValue(this.website);
      }
    });
  }

  private formatPost() {
    const post = {
      id: this.form.value.id,
      company_id: this.form.value.company_id,
      name: this.form.value.name,
      url: this.form.value.url,
      locales: <any[]>[],
      active: this.form.value.active,
      webhook_active: this.form.value.webhook_active,
      webhook_url: this.form.value.webhook_url,
      webhook_secret: this.form.value.webhook_secret,
    };

    const localesArray: FormArray = <FormArray>this.form.get('locales');
    localesArray.controls.forEach((formGroup: FormGroup) => {
      if (formGroup.get('selected').value) {
        post.locales.push({
          id: formGroup.get('id').value,
        });
      }
    });

    return post;
  }
}
